import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet";
import { Link, useNavigate } from "react-router-dom";
import { Tooltip, message } from "antd";
import DataParser from "../../../utlits/Dataparser";
import LeadStatusServices from "../../../Services/LeadStatusServices";
import secureLocalStorage from "react-secure-storage";
import { paginate } from "../../../utlits/Paginate.js";
import Pagination from "../../Reuseable/Pagination";
import StatusBadge from "../../Reuseable/StatusBadge";
import TableSkeleton from "../../Reuseable/TableSkeleton";

message.config({ zIndex: 1000000001 });

const LeadStatus = () => {
  const [leadStatusList, setLeadStatusList] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(15);
  const [userPermissions, setUserPermissions] = useState([]);
  const [isLoading, setLoading] = useState(false);
  const [statusName, setStatusName] = useState("");
  const [statusEditName, setStatusEditName] = useState("");
  const [error, setError] = useState("");
  const [editValue, setEditValue] = useState(null);
  const [statusColor, setStatusColor] = useState("#0DDFAB");
  const [statusEditColor, setStatusEditColor] = useState("#0DDFAB");
  const [itemToDelete, setItemToDelete] = useState(null);

  const companyId = DataParser.CompanyId();
  const navigate = useNavigate();

  const getData = () => {
    setLoading(true);
    LeadStatusServices.getLeadStatuses(companyId)
      .then((res) => {
        if (res.data === "Un-Athunticated request") {
          message.error("Session expired! Please log in again.");
          secureLocalStorage.removeItem("adminInfo");
          secureLocalStorage.removeItem("admins");
          navigate("/");
          return;
        }
        const data = res.data.leads_status || [];
        setLeadStatusList(data);
      })
      .catch((err) => {
        console.error("Error fetching lead statuses:", err);
        message.error("Failed to load lead statuses. Please try again!");
      })
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    getData();
    const upermission = DataParser.CompanyUserPermissions();
    setUserPermissions(upermission);
  }, []);

  useEffect(() => {
    if (pageSize > 15) setCurrentPage(1);
  }, [pageSize]);

  const handlePageChange = (e, page) => {
    e.preventDefault();
    setCurrentPage(page);
  };

  const clearCreateState = () => {
    setStatusName("");
    setStatusColor("#0DDFAB");
    setError("");
  };

  const clearEditState = () => {
    setStatusEditName("");
    setStatusEditColor("#0DDFAB");
    setEditValue(null);
    setError("");
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (statusName.trim() === "") {
      setError("Status name is required!");
      return;
    }

    const payload = { name: statusName.trim(), color: statusColor, company_id: companyId };

    LeadStatusServices.createLeadStatus(payload)
      .then((res) => {
        if (res.data === "Un-Athunticated request") {
          message.error("Session expired! Please log in again.");
          secureLocalStorage.removeItem("adminInfo");
          secureLocalStorage.removeItem("admins");
          navigate("/");
        } else if (res.Good === true) {
          message.success("Lead status created successfully!");
          const link = document.getElementById("closeCreateLeadStatus");
          if (link) link.click();
          setStatusName("");
          setStatusColor("#0DDFAB");
          getData();
        } else {
          message.warning("Could not create lead status. Please try again!");
        }
      })
      .catch((err) => {
        console.error("Create error:", err);
        message.error(
          err.response?.data?.message || "An error occurred while creating the lead status."
        );
      });
  };

  const handleOpenUpdate = (e, item) => {
    e.preventDefault();
    setStatusEditName(item.name);
    setStatusEditColor(item.color || "#0DDFAB");
    setEditValue(item);
    setError("");
  };

  const handleEditSubmit = (e) => {
    e.preventDefault();

    if (statusEditName.trim() === "") {
      setError("Status name is required!");
      return;
    }

    const id = editValue.id;
    const payload = { name: statusEditName.trim(), color: statusEditColor };

    LeadStatusServices.updateLeadStatus(id, payload)
      .then((res) => {
        if (res.data === "Un-Athunticated request") {
          message.error("Session expired! Please log in again.");
          secureLocalStorage.removeItem("adminInfo");
          secureLocalStorage.removeItem("admins");
          navigate("/");
        } else if (res.Good === true) {
          message.success("Lead status updated successfully!");
          const link = document.getElementById("closeEditLeadStatus");
          if (link) link.click();
          setStatusEditName("");
          getData();
        } else {
          message.warning("Could not update lead status. Please try again!");
        }
      })
      .catch((err) => {
        console.error("Update error:", err);
        message.error(
          err.response?.data?.message || "An error occurred while updating the lead status."
        );
      });
  };

  const handleDelete = (e, item) => {
    e.preventDefault();
    setItemToDelete(item);
    document.getElementById("openDeleteLeadStatusModal").click();
  };

  const confirmDelete = async () => {
    if (!itemToDelete) return;
    const id = itemToDelete.id;
    setLeadStatusList((prev) => prev.filter((el) => el.id !== id));
    document.getElementById("closeLeadStatusDeleteModal").click();
    try {
      const res = await LeadStatusServices.deleteLeadStatus(id);
      if (res.data === "Un-Athunticated request") {
        secureLocalStorage.removeItem("adminInfo");
        secureLocalStorage.removeItem("admins");
        navigate("/");
      } else {
        message.success("Lead status deleted successfully!");
      }
      getData();
    } catch (err) {
      console.error("Delete error:", err);
      message.error("Failed to delete lead status. Please try again!");
      getData();
    }
    setItemToDelete(null);
  };

  const paginatedData = paginate(leadStatusList, currentPage, pageSize);

  return (
    <>
      <Helmet>
        <title>Lead Status - Planit</title>
      </Helmet>
      <div className="main-content">
        <div className="page-content Pagebody" style={{ backgroundColor: "#fff", minHeight: "100vh" }}>
          <div className="container-fluid">
            <div className="row">
              <div className="col-12 px-0">
                <div className="d-flex align-items-end justify-content-end" style={{ padding: "0 0 12px 0" }}>
                  <div className="d-flex flex-column align-items-end" style={{ gap: "6px" }}>
                    <ol className="breadcrumb mb-0">
                      <li className="breadcrumb-item">
                        <Link to={`/${DataParser.CompanyIdentifierRole()}/employee-attendance`}>Home</Link>
                      </li>
                      <li className="breadcrumb-item active">
                        <Link to={`/${DataParser.CompanyIdentifierRole()}/settings`}>Settings</Link>
                      </li>
                      <li className="breadcrumb-item active">Lead Status</li>
                    </ol>
                    {userPermissions.indexOf("leadstatus-create") !== -1 ? (
                      <button
                        type="button"
                        className="btn btn-primary waves-effect waves-light"
                        style={{ borderRadius: "5px" }}
                        data-bs-toggle="modal"
                        data-bs-target="#createLeadStatusModalDialog"
                      >
                        Create Lead Status
                      </button>
                    ) : (
                      <button
                        type="button"
                        className="btn btn-primary waves-effect waves-light"
                        disabled
                        style={{ opacity: 0.6, cursor: "not-allowed", borderRadius: "5px" }}
                      >
                        Create Lead Status
                      </button>
                    )}
                  </div>
                </div>
              </div>

              <div className="col-lg-12 px-0">
                <div className="card" style={{ backgroundColor: "#fff", boxShadow: "none", borderRadius: 0, border: "none" }}>
                  <div className="card-body p-0">
                    <div className="table-responsive listing-table-wrapper">
                      <table className="table mb-0 custom-table listing-table">
                        <thead>
                          <tr className="tableHeaderbg">
                            <th>Sr#</th>
                            <th>Status Name</th>
                            <th>Status</th>
                            <th>Action</th>
                          </tr>
                        </thead>
                        <tbody>
                          {isLoading ? (
                            <TableSkeleton cols={4} rows={6} />
                          ) : paginatedData.length > 0 ? (
                            paginatedData.map((item, index) => (
                              <tr key={item.id}>
                                <td>{index + 1}</td>
                                <td style={{ color: item.color || "#000", fontWeight: "600" }}>{item.name}</td>
                                <td>
                                  {userPermissions.indexOf("leadstatus-edit") !== -1 ? (
                                    <StatusBadge
                                      item={item}
                                      api={LeadStatusServices.statusChange}
                                    />
                                  ) : (
                                    <>
                                      {item.status === "1" ? (
                                        <span className="badge bg-success text-white">Active</span>
                                      ) : (
                                        <span className="badge bg-danger text-white">Deactive</span>
                                      )}
                                    </>
                                  )}
                                </td>
                                <td className="tableset">
                                  {userPermissions.indexOf("leadstatus-delete") !== -1 ? (
                                    <Link
                                      to="#"
                                      className="cursor-pointer iconscolor"
                                      onClick={(e) => handleDelete(e, item)}
                                    >
                                      <Tooltip title="Delete Lead Status">
                                        <i className="uil-trash iconscolor" />
                                      </Tooltip>
                                    </Link>
                                  ) : (
                                    <span
                                      className="uil-trash iconscolor text-muted"
                                      style={{ cursor: "not-allowed", opacity: 0.5 }}
                                      title="You don't have permission to delete"
                                    />
                                  )}
                                  {userPermissions.indexOf("leadstatus-edit") !== -1 ? (
                                    <Link
                                      to="#"
                                      className="cursor-pointer iconscolor"
                                      onClick={(e) => handleOpenUpdate(e, item)}
                                      data-bs-toggle="modal"
                                      data-bs-target="#editLeadStatusModalDialog"
                                    >
                                      <Tooltip title="Edit Lead Status">
                                        <i className="uil-pen iconscolor" />
                                      </Tooltip>
                                    </Link>
                                  ) : (
                                    <span
                                      className="uil-pen iconscolor text-muted"
                                      style={{ cursor: "not-allowed", opacity: 0.5 }}
                                      title="You don't have permission to edit"
                                    />
                                  )}
                                </td>
                              </tr>
                            ))
                          ) : (
                            <tr>
                              <td colSpan="4" className="text-center">
                                Add your first lead status by clicking the 'Create Lead Status' button.
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>

                      {leadStatusList.length > pageSize && (
                        <div className="d-flex justify-content-center mt-4">
                          <Pagination
                            itemCount={leadStatusList.length}
                            currentPage={currentPage}
                            pageSize={pageSize}
                            onPageChange={handlePageChange}
                          />
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Hidden trigger for delete modal */}
        <div id="openDeleteLeadStatusModal" data-bs-toggle="modal" data-bs-target="#deleteLeadStatusModal" style={{ display: "none" }} />

        {/* Delete Confirmation Modal */}
        <div id="deleteLeadStatusModal" className="modal fade" tabIndex={-1} role="dialog" aria-hidden="true" data-bs-keyboard="false">
          <div className="modal-dialog modal-dialog-centered" style={{ display: "flex", alignItems: "center", minHeight: "calc(100vh - 3.5rem)", margin: "auto" }}>
            <div className="modal-content" style={{ borderRadius: "12px", border: "none", padding: "8px 4px" }}>
              <div className="modal-header border-0 pb-1">
                <h5 className="modal-title fw-semibold" style={{ fontSize: "16px" }}>Delete Lead Status</h5>
                <button type="button" className="btn-close" id="closeLeadStatusDeleteModal" data-bs-dismiss="modal" aria-label="Close" onClick={() => setItemToDelete(null)} />
              </div>
              <div className="modal-body pt-2 pb-2">
                <p style={{ fontSize: "14px", color: "#1D1D1D", marginBottom: "6px" }}>Are you sure you want to delete this lead status?</p>
                <p style={{ fontSize: "13px", color: "#ff4d4f", marginBottom: 0 }}>
                  <i className="uil uil-exclamation-triangle me-1" />
                  This action cannot be undone.
                </p>
              </div>
              <div className="modal-footer border-0 pt-1">
                <button type="button" className="btn btn-danger waves-effect waves-light" onClick={confirmDelete} style={{ borderRadius: "5px", fontSize: "13px" }}>
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Create Modal */}
        <div
          id="createLeadStatusModalDialog"
          className="modal fade"
          tabIndex={-1}
          role="dialog"
          aria-hidden="true"
          data-bs-keyboard="false"
        >
          <form onSubmit={handleSubmit}>
            <div className="modal-dialog modal-dialog-centered" style={{ display: "flex", alignItems: "center", minHeight: "calc(100vh - 3.5rem)", margin: "auto" }}>
              <div className="modal-content" style={{ borderRadius: "12px", border: "none", padding: "8px 4px" }}>
                <div className="modal-header border-0 pb-1">
                  <h5 className="modal-title fw-semibold" style={{ fontSize: "16px" }}>Create Lead Status</h5>
                  <button
                    type="button"
                    className="btn-close"
                    id="closeCreateLeadStatus"
                    data-bs-dismiss="modal"
                    aria-label="Close"
                    onClick={clearCreateState}
                  />
                </div>
                <div className="modal-body pt-2 pb-2">
                  <div className="float-input-wrap mb-3">
                    <input
                      type="text"
                      className={error ? "is-invalid" : ""}
                      id="statusName"
                      placeholder=" "
                      value={statusName}
                      onChange={(e) => {
                        setStatusName(e.target.value);
                        if (e.target.value.trim() !== "") setError("");
                      }}
                    />
                    <label htmlFor="statusName">Status Name*</label>
                  </div>
                  {error && <small className="text-danger mt-1 d-block">{error}</small>}
                  <div className="d-flex align-items-center" style={{ gap: "10px", marginTop: "8px" }}>
                    <span style={{ fontSize: "13px", color: "#555" }}>Status Color</span>
                    <label htmlFor="statusColor" style={{ cursor: "pointer", margin: 0 }}>
                      <span
                        style={{
                          display: "inline-block",
                          width: "24px",
                          height: "24px",
                          borderRadius: "50%",
                          backgroundColor: statusColor,
                          border: "2px solid #d0d0d0",
                          verticalAlign: "middle",
                        }}
                      />
                    </label>
                    <input
                      type="color"
                      id="statusColor"
                      value={statusColor}
                      onChange={(e) => setStatusColor(e.target.value)}
                      style={{ width: 0, height: 0, opacity: 0, border: "none", padding: 0 }}
                    />
                  </div>
                </div>
                <div className="modal-footer border-0 pt-1">
                  <button
                    type="button"
                    onClick={handleSubmit}
                    className="btn btn-primary waves-effect waves-light"
                    style={{ borderRadius: "5px", fontSize: "13px" }}
                  >
                    + Create Lead Status
                  </button>
                </div>
              </div>
            </div>
          </form>
        </div>

        {/* Edit Modal */}
        <div
          id="editLeadStatusModalDialog"
          className="modal fade"
          tabIndex={-1}
          role="dialog"
          aria-hidden="true"
          data-bs-keyboard="false"
        >
          <form onSubmit={handleEditSubmit}>
            <div className="modal-dialog modal-dialog-centered" style={{ display: "flex", alignItems: "center", minHeight: "calc(100vh - 3.5rem)", margin: "auto" }}>
              <div className="modal-content" style={{ borderRadius: "12px", border: "none", padding: "8px 4px" }}>
                <div className="modal-header border-0 pb-1">
                  <h5 className="modal-title fw-semibold" style={{ fontSize: "16px" }}>Edit Lead Status</h5>
                  <button
                    type="button"
                    className="btn-close"
                    id="closeEditLeadStatus"
                    data-bs-dismiss="modal"
                    aria-label="Close"
                    onClick={clearEditState}
                  />
                </div>
                <div className="modal-body pt-2 pb-2">
                  <div className="float-input-wrap mb-3">
                    <input
                      type="text"
                      className={error ? "is-invalid" : ""}
                      id="editStatusName"
                      placeholder=" "
                      value={statusEditName}
                      onChange={(e) => {
                        setStatusEditName(e.target.value);
                        if (e.target.value.trim() !== "") setError("");
                      }}
                    />
                    <label htmlFor="editStatusName">Status Name*</label>
                  </div>
                  {error && <small className="text-danger mt-1 d-block">{error}</small>}
                  <div className="d-flex align-items-center" style={{ gap: "10px", marginTop: "8px" }}>
                    <span style={{ fontSize: "13px", color: "#555" }}>Status Color</span>
                    <label htmlFor="statusEditColor" style={{ cursor: "pointer", margin: 0 }}>
                      <span
                        style={{
                          display: "inline-block",
                          width: "24px",
                          height: "24px",
                          borderRadius: "50%",
                          backgroundColor: statusEditColor,
                          border: "2px solid #d0d0d0",
                          verticalAlign: "middle",
                        }}
                      />
                    </label>
                    <input
                      type="color"
                      id="statusEditColor"
                      value={statusEditColor}
                      onChange={(e) => setStatusEditColor(e.target.value)}
                      style={{ width: 0, height: 0, opacity: 0, border: "none", padding: 0 }}
                    />
                  </div>
                </div>
                <div className="modal-footer border-0 pt-1">
                  <button
                    type="button"
                    onClick={handleEditSubmit}
                    className="btn btn-primary waves-effect waves-light"
                    style={{ borderRadius: "5px", fontSize: "13px" }}
                  >
                    Save Changes
                  </button>
                </div>
              </div>
            </div>
          </form>
        </div>
      </div>
    </>
  );
};

export default LeadStatus;
